import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DoubleCountingPreventionPanel = ({ onResolveConflict }) => {
  const [selectedConflict, setSelectedConflict] = useState(null);

  const mockConflicts = [
    {
      id: 'conflict-001',
      creditId: 'VCS-2847-2023-001',
      serialNumber: 'VCS-2847-2023-001-001-001-VCU',
      conflictType: 'corporate_ndc_overlap',
      severity: 'high',
      description: 'Credit claimed in corporate inventory while host country includes in NDC accounting',
      hostCountry: 'Brazil',
      corporateEntity: 'Global Manufacturing Corp',
      ndcContribution: 'Included in Brazil NDC 2025 submission',
      detectedDate: '2025-01-20T09:30:00Z',
      status: 'unresolved',
      recommendedAction: 'Apply corresponding adjustment or exclude from corporate claims',
      quantity: 1000
    },
    {
      id: 'conflict-002',
      creditId: 'GS-1456-2023-002',
      serialNumber: 'GS-1456-2023-002-001-001-VER',
      conflictType: 'multiple_corporate_claims',
      severity: 'medium',
      description: 'Credit claimed by multiple corporate entities in their inventories',
      hostCountry: 'Colombia',
      corporateEntity: 'Tech Solutions Inc, Green Energy Ltd',
      ndcContribution: 'Not included in NDC',
      detectedDate: '2025-01-19T14:15:00Z',
      status: 'under_review',
      recommendedAction: 'Verify ownership chain and resolve duplicate claims',
      quantity: 500
    },
    {
      id: 'conflict-003',
      creditId: 'ACR-789-2022-003',
      serialNumber: 'ACR-789-2022-003-001-001-CRT',
      conflictType: 'ndc_double_counting',
      severity: 'critical',
      description: 'Credit counted in both host country NDC and international transfer',
      hostCountry: 'Peru',
      corporateEntity: 'Sustainable Corp',
      ndcContribution: 'Double-counted in Peru NDC and international market',
      detectedDate: '2025-01-18T11:45:00Z',
      status: 'escalated',
      recommendedAction: 'Immediate corresponding adjustment required',
      quantity: 750
    }
  ];

  const getSeverityBadge = (severity) => {
    const config = {
      critical: { color: 'text-error bg-error/10 border-error/20', icon: 'AlertTriangle' },
      high: { color: 'text-warning bg-warning/10 border-warning/20', icon: 'AlertCircle' },
      medium: { color: 'text-accent bg-accent/10 border-accent/20', icon: 'Info' },
      low: { color: 'text-muted-foreground bg-muted border-border', icon: 'Minus' }
    };

    const { color, icon } = config?.[severity] || config?.medium;

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${color}`}>
        <Icon name={icon} size={12} />
        <span className="capitalize">{severity}</span>
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      unresolved: { color: 'text-error bg-error/10', icon: 'XCircle' },
      under_review: { color: 'text-warning bg-warning/10', icon: 'Clock' },
      escalated: { color: 'text-error bg-error/20', icon: 'AlertTriangle' },
      resolved: { color: 'text-success bg-success/10', icon: 'CheckCircle' }
    };

    const { color, icon } = config?.[status] || config?.unresolved;

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        <Icon name={icon} size={12} />
        <span className="capitalize">{status?.replace('_', ' ')}</span>
      </span>
    );
  };

  const handleResolveConflict = (conflictId, action) => {
    onResolveConflict(conflictId, action);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-error" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-card-foreground">Double-Counting Prevention</h2>
            <p className="text-sm text-muted-foreground">Automated conflict detection between corporate inventory and NDC claims</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" iconName="Download">
            Export Conflicts
          </Button>
          <Button variant="outline" size="sm" iconName="RefreshCw">
            Refresh Scan
          </Button>
        </div>
      </div>
      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-error/5 border border-error/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="AlertTriangle" size={16} className="text-error" />
            <span className="text-xs font-medium text-error">Critical Conflicts</span>
          </div>
          <div className="text-2xl font-bold text-error">1</div>
          <div className="text-xs text-error/80">Immediate action required</div>
        </div>
        <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="AlertCircle" size={16} className="text-warning" />
            <span className="text-xs font-medium text-warning">High Priority</span>
          </div>
          <div className="text-2xl font-bold text-warning">1</div>
          <div className="text-xs text-warning/80">Review within 24h</div>
        </div>
        <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Info" size={16} className="text-accent" />
            <span className="text-xs font-medium text-accent">Medium Priority</span>
          </div>
          <div className="text-2xl font-bold text-accent">1</div>
          <div className="text-xs text-accent/80">Review within week</div>
        </div>
        <div className="bg-success/5 border border-success/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-xs font-medium text-success">Resolved</span>
          </div>
          <div className="text-2xl font-bold text-success">47</div>
          <div className="text-xs text-success/80">This month</div>
        </div>
      </div>
      {/* Conflicts List */}
      <div className="space-y-4">
        {mockConflicts?.map((conflict) => (
          <div key={conflict?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  conflict?.severity === 'critical' ? 'bg-error' :
                  conflict?.severity === 'high' ? 'bg-warning' :
                  conflict?.severity === 'medium' ? 'bg-accent' : 'bg-muted-foreground'
                }`}></div>
                <div>
                  <div className="font-mono text-sm font-medium text-card-foreground">{conflict?.serialNumber}</div>
                  <div className="text-xs text-muted-foreground">Detected: {formatDate(conflict?.detectedDate)}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getSeverityBadge(conflict?.severity)}
                {getStatusBadge(conflict?.status)}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-card-foreground mb-2">{conflict?.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="font-medium text-muted-foreground">Host Country:</span>
                  <span className="ml-2 text-card-foreground">{conflict?.hostCountry}</span>
                </div>
                <div>
                  <span className="font-medium text-muted-foreground">Quantity:</span>
                  <span className="ml-2 text-card-foreground">{conflict?.quantity?.toLocaleString()} tCO2e</span>
                </div>
                <div>
                  <span className="font-medium text-muted-foreground">Corporate Entity:</span>
                  <span className="ml-2 text-card-foreground">{conflict?.corporateEntity}</span>
                </div>
                <div>
                  <span className="font-medium text-muted-foreground">NDC Status:</span>
                  <span className="ml-2 text-card-foreground">{conflict?.ndcContribution}</span>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-3 mb-4">
              <div className="flex items-start space-x-2">
                <Icon name="Lightbulb" size={16} className="text-accent mt-0.5" />
                <div>
                  <div className="text-xs font-medium text-card-foreground mb-1">Recommended Resolution:</div>
                  <div className="text-xs text-muted-foreground">{conflict?.recommendedAction}</div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  onClick={() => setSelectedConflict(conflict?.id)}
                >
                  View Details
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileText"
                  onClick={() => handleResolveConflict(conflict?.id, 'generate_guidance')}
                >
                  Resolution Guide
                </Button>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="success"
                  size="sm"
                  iconName="CheckCircle"
                  onClick={() => handleResolveConflict(conflict?.id, 'apply_ca')}
                  disabled={conflict?.status === 'resolved'}
                >
                  Apply CA
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="UserCheck"
                  onClick={() => handleResolveConflict(conflict?.id, 'escalate')}
                >
                  Escalate
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Prevention Guidelines */}
      <div className="mt-6 bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-primary mb-2">Prevention Guidelines</h4>
            <ul className="text-xs text-primary/80 space-y-1">
              <li>• Verify corresponding adjustment status before international transfer</li>
              <li>• Ensure host country authorization excludes credits from NDC accounting</li>
              <li>• Implement 1-credit-1-claim enforcement across all systems</li>
              <li>• Monitor registry updates for NDC inclusion changes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoubleCountingPreventionPanel;